import {DeserializedRecord, deserializeItem, Deserializer, deserializeSingleRelationship, JSONAPIRecord, JSONAPISingleRelationship} from "../Helpers";
import {deserializePhysician, Physician} from "../Physician";
import {deserializePatientMeasurementTriggerEvent, Event as PatientMeasurementTriggerEvent} from "./measurement/Event";
import {deserializeEcgTriggerEvent, EcgTriggerEvent} from "./measurement/ecg/Event";
import {deserializeEvent as deserializePatientSurveyTriggerEvent, PatientSurveyTriggerEvent} from "../patient/survey/triggers/Events";

type PatientTriggerEventStatus = 'open' | 'closed';
type TriggerEvent = PatientMeasurementTriggerEvent | EcgTriggerEvent | PatientSurveyTriggerEvent;

enum TriggerEventType {
  MeasurementTriggerEvent = 'measurement_trigger_event',
  MeasurementEcgTriggerEvent = 'measurement_ecg_trigger_event',
  SurveyTriggerEvent = 'survey_trigger_event'
}

interface PatientTriggerEvent extends DeserializedRecord {
  patientId?: string,
  patientTriggerId?: string,
  patientTriggerDescription?: string,
  patientName?: string,
  seenAt: Date,
  status: PatientTriggerEventStatus,
  comment: string,
  medicalAction: boolean,
  physician?: Physician,
  subject: PatientMeasurementTriggerEvent | EcgTriggerEvent | PatientSurveyTriggerEvent
}

interface PatientTriggerEventData extends JSONAPIRecord {
  relationships: {
    physician: JSONAPISingleRelationship,
    subject: JSONAPISingleRelationship
  }
}

const isPatientMeasurementTriggerEvent = (
  obj: TriggerEvent
): obj is PatientMeasurementTriggerEvent => (
  obj &&
  obj.type === "measurement_trigger_event"
);

const isPatientSurveyTriggerEvent = (
  obj: TriggerEvent
): obj is PatientMeasurementTriggerEvent => (
  obj &&
  obj.type === "survey_trigger_event"
);

const isPatientMeasurementEcgTriggerEvent = (
  obj: TriggerEvent
): obj is EcgTriggerEvent => (
  obj &&
  obj.type === "measurement_ecg_trigger_event"
);

const deserializerBySubjectType = (type: string) => {
  switch (type) {
    case 'measurement_trigger_event':
      return deserializePatientMeasurementTriggerEvent;
    case 'measurement_ecg_trigger_event':
      return deserializeEcgTriggerEvent;
    case 'survey_trigger_event':
      return deserializePatientSurveyTriggerEvent;
    default:
      return deserializeItem;
  }
};

const deserializePatientTriggerEvent: Deserializer<PatientTriggerEvent, PatientTriggerEventData> = (data, included) => {
  const physician = deserializeSingleRelationship<Physician>(data.relationships.physician, included, deserializePhysician);

  const subject = deserializeSingleRelationship<TriggerEvent>(
    data.relationships.subject,
    included,
    deserializerBySubjectType(data.relationships.subject?.data?.type)
  );

  return {
    ...deserializeItem(data),
    physician,
    subject
  };
};


export {
  PatientTriggerEvent, PatientTriggerEventData,
  deserializePatientTriggerEvent,
  TriggerEventType,
  isPatientMeasurementTriggerEvent, isPatientSurveyTriggerEvent, isPatientMeasurementEcgTriggerEvent
};
